import type { RenderComponentProps } from "replo-runtime/shared/types";
import type { Component } from "schemas/component";

import React from "react";

import unescape from "lodash-es/unescape";

import { LOADING_SHIMMER_STYLE } from "../../../utils/alchemy-component";
import isLoadingProduct from "../../../utils/isLoadingProduct";
import { ReploComponent } from "../../ReploComponent";
import { useBundleBuilder } from "../BundleBuilderContext";
import { ProductTileProvider } from "./ProductTileContext";

function getProductProps(props: Component["props"]) {
  const productId =
    typeof props.productId === "string" ? props.productId : undefined;
  const variantId =
    typeof props.variantId === "string" ? props.variantId : undefined;

  return {
    productId,
    variantId,
  };
}

export const ProductTile: React.FC<RenderComponentProps> = ({
  component,
  context,
  componentAttributes,
}) => {
  const { productId, variantId } = getProductProps(component.props);
  const { bundleTargetType, getProductData } = useBundleBuilder();
  const productData = productId ? getProductData(productId) : undefined;
  const shouldShowLoadingShimmer = productData && isLoadingProduct(productData);
  const isProductTarget = bundleTargetType === "products";
  const defaultVariantId = variantId ?? productData?.variants[0]?.id ?? null;
  const [selectedVariantId, setSelectedVariantId] = React.useState<
    string | null
  >(defaultVariantId ? String(defaultVariantId) : null);
  const isHorizontal = component.props.layout === "horizontal";
  React.useEffect(() => {
    const defaultVariantId = variantId ?? productData?.variants[0]?.id ?? null;
    if (defaultVariantId) {
      setSelectedVariantId(defaultVariantId ? String(defaultVariantId) : null);
    }
  }, [productData, variantId]);

  if (!productData) {
    return null;
  }
  if (!selectedVariantId) {
    return null;
  }

  const selectedVariant = productData.variants.find(
    (v) => String(v.id) === String(selectedVariantId),
  );

  if (!selectedVariant) {
    return null;
  }

  const imageUrl = selectedVariant.featuredImage || productData.featured_image;
  const item = {
    title: isProductTarget ? productData.title : selectedVariant.title,
    price: selectedVariant.price,
    compareAtPrice: selectedVariant.compareAtPrice,
  };

  function renderChildren() {
    return (
      productData &&
      component.children?.map((component) => {
        switch (component.type) {
          case "bundleProductVariantSelector":
            // NOTE (Matt 2025-05-09): We should only show this component if the targetType is "products"
            // And then we should only show it for any options that have more than 1 value.
            return (
              bundleTargetType === "products" &&
              productData.options.map((option, index) => {
                if (option.values?.length && option.values.length > 1) {
                  return (
                    <div
                      key={option.key}
                      style={
                        isHorizontal
                          ? { gridColumn: "2 / 4", gridRow: 2 + index }
                          : { width: "100%" }
                      }
                    >
                      <ReploComponent
                        component={{
                          ...component,
                          props: {
                            ...component.props,
                            optionKey: option.key,
                          },
                        }}
                        context={context}
                      />
                    </div>
                  );
                }
                return null;
              })
            );
          case "bundleAddToBundleButton":
            return (
              <div
                style={
                  isHorizontal
                    ? {
                        width: "100%",
                        gridColumn: "3",
                        gridRow: "1",
                        justifySelf: "end",
                      }
                    : { width: "100%" }
                }
              >
                <ReploComponent
                  key={component.id}
                  component={{
                    ...component,
                    props: { ...component.props, variant: "sm" },
                  }}
                  context={context}
                />
              </div>
            );
          default:
            return (
              <ReploComponent
                key={component.id}
                component={component}
                context={context}
              />
            );
        }
      })
    );
  }

  return (
    <ProductTileProvider
      productData={productData}
      selectedVariantId={selectedVariantId}
      onVariantChange={setSelectedVariantId}
    >
      {isHorizontal ? (
        <div
          {...componentAttributes}
          style={{
            display: "grid",
            gridTemplateColumns: "69px 1fr 114px",
            alignItems: "center",
            maxWidth: "100%",
            gap: "16px",
            ...componentAttributes.style,
            ...(shouldShowLoadingShimmer && LOADING_SHIMMER_STYLE),
          }}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt={item.title}
              style={{
                width: "56px",
                height: "56px",
                objectFit: "cover",
                borderRadius: "8px",
                gridColumn: "1",
              }}
            />
          )}
          <div
            style={{
              textAlign: "left",
              gridColumn: "2",
              alignSelf: "center",
              overflow: "hidden",
              whiteSpace: "wrap",
            }}
          >
            <h3
              style={{
                fontFamily: "inherit",
                fontSize: "16px",
                fontWeight: "600",
                lineHeight: 1.5,
                marginTop: 0,
                marginBottom: "2px",
              }}
            >
              {item.title}
            </h3>
            <div style={{ display: "flex", gap: "4px" }}>
              <h4
                style={{
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#6B7280",
                  margin: 0,
                }}
                dangerouslySetInnerHTML={{
                  __html: unescape(selectedVariant.displayPrice),
                }}
              />
              {selectedVariant.compareAtDisplayPrice && (
                <h4
                  style={{
                    fontWeight: "400",
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#6B7280",
                    textDecoration: "line-through",
                    margin: 0,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: unescape(selectedVariant.compareAtDisplayPrice),
                  }}
                />
              )}
            </div>
          </div>
          {renderChildren()}
        </div>
      ) : (
        <div
          {...componentAttributes}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: "100%",
            gap: "8px",
            ...componentAttributes.style,
            ...(shouldShowLoadingShimmer && LOADING_SHIMMER_STYLE),
          }}
        >
          <div
            style={{
              width: "100%",
              position: "relative",
              paddingBottom: "100%", // Square aspect ratio
              overflow: "hidden",
              borderRadius: "8px",
            }}
          >
            {imageUrl && (
              <img
                src={imageUrl}
                alt={item.title}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "4px",
                }}
              />
            )}
          </div>
          <div style={{ width: "100%", textAlign: "left" }}>
            <h3
              style={{
                fontFamily: "inherit",
                fontSize: "16px",
                fontWeight: "600",
                lineHeight: 1.5,
                marginTop: 0,
                marginBottom: "2px",
              }}
            >
              {item.title}
            </h3>
            <div style={{ display: "flex", gap: "4px" }}>
              <h4
                style={{
                  fontFamily: "inherit",
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#6B7280",
                  margin: 0,
                }}
                dangerouslySetInnerHTML={{
                  __html: unescape(selectedVariant.displayPrice),
                }}
              />
              {selectedVariant.compareAtDisplayPrice && (
                <h4
                  style={{
                    fontFamily: "inherit",
                    fontWeight: "400",
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: "#6B7280",
                    textDecoration: "line-through",
                    margin: 0,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: unescape(selectedVariant.compareAtDisplayPrice),
                  }}
                />
              )}
            </div>
          </div>
          {renderChildren()}
        </div>
      )}
    </ProductTileProvider>
  );
};
